/**
 * Encapsulates different variations iof item feedback UI and interfaces
 * with flux actions.
 */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import userAgent from 'js/constants/userAgent';

import type { Theme } from '@coursera/cds-core';
import { breakpoints, withTheme } from '@coursera/cds-core';

import type { CmlContent } from 'bundles/cml';
import { CMLUtils } from 'bundles/cml';
import {
  cancelDislikeItem,
  cancelLikeItem,
  dislikeItem,
  getItemFeedback,
  likeItem,
} from 'bundles/content-feedback/actions/ItemFeedbackActions';
import VideoItemFeedbackFlag from 'bundles/content-feedback/components/cds/item-feedback/VideoItemFeedbackFlag';
import Dislike from 'bundles/content-feedback/components/cds/like/Dislike';
import Like from 'bundles/content-feedback/components/cds/like/Like';
import type { ItemType } from 'bundles/content-feedback/constants/ItemTypes';
import type LikeFeedback from 'bundles/content-feedback/models/LikeFeedback';
import type { Item } from 'bundles/learner-progress/types/Item';
import type { ComputedItemType } from 'bundles/ondemand/lib/ComputedItem';
import type CourseStoreClass from 'bundles/ondemand/stores/CourseStore';
import { track as trackV3 } from 'bundles/page/lib/event-pulse/sdk';

type PropsFromCaller = {
  courseId: string;
  computedItem: Item | ComputedItemType;
  itemFeedbackType: ItemType;
  tooltipPlacement: string; // TODO tighten
  subItemId?: string;
  hideReportAnIssue?: boolean;
  /**
   * Relevant for course lecture item pages in the context of the "Report an Issue" button. We need this to set whether we want to cancel autoplay
   * of the video when a user is trying to report an issue.
   */
  setIsUserReportingIssue?: (isUserReportingIssue: boolean) => void;
};

type PropsFromStores = {
  likeFeedback: LikeFeedback;
  dislikeFeedback: LikeFeedback;
  feedbackItemId: string;
};

type PropsFromCDS = {
  theme: Theme;
};

type Stores = {
  CourseStore: CourseStoreClass;
  ItemFeedbackStore: $TSFixMe;
};

export type Props = PropsFromCaller & PropsFromStores & PropsFromCDS;

type State = { a11yActionMessageForAndroid: string | null };

export class ItemFeedbackContent extends React.Component<Props, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
    track: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof ItemFeedbackContent.contextTypes>;

  state: State = {
    a11yActionMessageForAndroid: null,
  };

  componentDidMount() {
    const { courseId, computedItem, subItemId } = this.props;
    const { executeAction } = this.context;
    const { id: itemId } = computedItem;

    executeAction(getItemFeedback, {
      courseId,
      itemId,
      subItemId,
    });
  }

  handleLike = (comment: CmlContent) => {
    const { courseId, computedItem, subItemId } = this.props;
    const { executeAction, track } = this.context;
    const { id: itemId } = computedItem;

    executeAction(likeItem, {
      courseId,
      itemId,
      comment,
      subItemId,
    });
    track('click.like');
    trackV3('send_content_feedback', { feedback: 'like_message' });
    this.setA11yActionMessageForAndroid('Like button, ticked');
  };

  handleLikeComment = (comment: CmlContent) => {
    const { courseId, computedItem, subItemId } = this.props;
    const { executeAction, track } = this.context;
    const { id: itemId } = computedItem;

    executeAction(likeItem, {
      courseId,
      itemId,
      comment,
      subItemId,
    });
    track('click.send_like_comment', {
      // eslint-disable-next-line camelcase
      feedback_length: CMLUtils.getLength(comment),
    });
  };

  handleLikeCancel = () => {
    const { courseId, computedItem, subItemId, likeFeedback } = this.props;
    const { executeAction, track } = this.context;
    const { id: itemId } = computedItem;
    const { comment } = likeFeedback;

    executeAction(cancelLikeItem, {
      courseId,
      itemId,
      comment,
      subItemId,
    });
    track('click.cancel_like');
    trackV3('send_content_feedback', { feedback: 'cancel_like_message' });
    this.setA11yActionMessageForAndroid('Like button, unticked');
  };

  handleDislike = (comment: CmlContent) => {
    const { courseId, computedItem, subItemId } = this.props;
    const { executeAction, track } = this.context;
    const { id: itemId } = computedItem;

    executeAction(dislikeItem, {
      courseId,
      itemId,
      comment,
      subItemId,
    });
    track('click.dislike');
    trackV3('send_content_feedback', { feedback: 'dislike_message' });
    this.setA11yActionMessageForAndroid('Dislike button, ticked');
  };

  handleDislikeComment = (comment: CmlContent) => {
    const { courseId, computedItem, subItemId } = this.props;
    const { executeAction, track } = this.context;
    const { id: itemId } = computedItem;

    executeAction(dislikeItem, {
      courseId,
      itemId,
      comment,
      subItemId,
    });
    track('click.send_dislike_comment', {
      // eslint-disable-next-line camelcase
      feedback_length: CMLUtils.getLength(comment),
    });
  };

  handleDislikeCancel = () => {
    const { courseId, computedItem, subItemId, dislikeFeedback } = this.props;
    const { executeAction, track } = this.context;
    const { id: itemId } = computedItem;
    const { comment } = dislikeFeedback;

    executeAction(cancelDislikeItem, {
      courseId,
      itemId,
      comment,
      subItemId,
    });
    track('click.cancel_dislike');
    trackV3('send_content_feedback', { feedback: 'cancel_dislike_message' });
    this.setA11yActionMessageForAndroid('Dislike button, unticked');
  };

  /* This is a temporary workaround to announce the action message for
    the Like/Dislike button until Android Talkback fully supports aria-pressed */
  setA11yActionMessageForAndroid = (message: string) => {
    if (userAgent.isAndroid) {
      this.setState({ a11yActionMessageForAndroid: message });
    }
  };

  render() {
    const {
      computedItem,
      itemFeedbackType,
      feedbackItemId,
      tooltipPlacement,
      likeFeedback,
      dislikeFeedback,
      hideReportAnIssue,
      setIsUserReportingIssue,
    } = this.props;
    const { a11yActionMessageForAndroid } = this.state;

    if (computedItem.id !== feedbackItemId) {
      return null;
    }

    const itemFeedbackContent = css`
      margin-left: -8px;
      position: relative;

      ${breakpoints.down('md')} {
        flex-wrap: wrap;
      }
    `;

    return (
      <div className="rc-ItemFeedbackContent horizontal-box" css={itemFeedbackContent}>
        <Like
          key="Like"
          comment={likeFeedback.comment}
          selected={likeFeedback.isSelected}
          tooltipPlacement={tooltipPlacement}
          onSelect={this.handleLike}
          onDeselect={this.handleLikeCancel}
          itemFeedbackType={itemFeedbackType}
        />
        <Dislike
          key="Dislike"
          comment={dislikeFeedback.comment}
          selected={dislikeFeedback.isSelected}
          tooltipPlacement={tooltipPlacement}
          onSelect={this.handleDislike}
          onDeselect={this.handleDislikeCancel}
          itemFeedbackType={itemFeedbackType}
        />
        {!hideReportAnIssue && (
          <VideoItemFeedbackFlag
            tooltipPlacement="top"
            setIsUserReportingIssue={setIsUserReportingIssue}
            computedItem={computedItem}
            itemFeedbackType={itemFeedbackType}
            eventNameSubmit="core_lex_report_bottom_issue_submit"
            eventNameRemove="core_lex_report_bottom_issue_remove"
            eventNameOpen="core_lex_report_bottom_issue"
          />
        )}
        {userAgent.isAndroid && (
          <div
            aria-live="polite"
            aria-atomic="true"
            aria-relevant="text"
            className="screenreader-only"
            data-testid="a11y-action-message"
          >
            {a11yActionMessageForAndroid}
          </div>
        )}
      </div>
    );
  }
}

export default compose<Props, PropsFromCaller>(
  connectToStores<PropsFromCaller, PropsFromCaller, Stores>(
    ['CourseStore', 'ItemFeedbackStore'],
    ({ CourseStore, ItemFeedbackStore }: Stores) => ({
      courseId: CourseStore.getCourseId(),
      likeFeedback: ItemFeedbackStore.getLikeFeedback(),
      dislikeFeedback: ItemFeedbackStore.getDislikeFeedback(),
      feedbackItemId: ItemFeedbackStore.getItemId(),
    })
  ),
  withTheme
)(ItemFeedbackContent);
